/*
  Карточка организации: три поля, от которых зависит, примет ли суд заявление.

  Вынесено из `pages/SettingsPage.jsx` 09.09.2026, когда настройки разложили по
  вкладкам. До этого девятнадцать полей четырёх разных назначений шли одной
  простынёй, и три кнопки «Сохранить» отличались только подписью.

  Ширины полей - по длине значения, а не по ширине экрана: ИНН это десять цифр,
  код региона две. Поле в тысячу пикселей под двузначное число читается как
  «впишите сюда абзац» и прячет опечатку (замер 09.09.2026).
*/

import { useState } from 'react'

import { parseCount } from '../api/parseCount.js'
import { LockedForm } from './AccessContext.jsx'
import { Field } from './Common.jsx'

/* Границы дня те же, что стояли на самом поле до 28.10.2026 (`min`/`max`). */
const DUE_DAY = { min: 1, max: 31 }

/* `problems` - причины отказа по полям (`fieldErrors`) - встают под самим полем
   (аудит 26.10.2026, причина 6). */
export default function SettingsOrganisation({
  organisation,
  onSave,
  problems = {},
  saving = false,
}) {
  // Опечатка в сроке оплаты не уходит на сервер пустотой (то есть «срок снят»): причина
  // встаёт под полем, сохранение не выполняется (аудит 26.10.2026, строка 299).
  const [dayError, setDayError] = useState(null)
  return (
    <LockedForm
      busy={saving}
      onSubmit={(event) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        const day = parseCount(form.get('payment_due_day'), DUE_DAY)
        setDayError(day.error)
        if (day.error) return
        onSave({
          name: form.get('name'),
          short_name: form.get('short_name') || null,
          inn: form.get('inn') || null,
          region_code: form.get('region_code') || null,
          payment_due_day: day.value,
          // Пустая строка выключает письмо с отчётом (строка 36 реестра, 30.10.2026).
          report_email: String(form.get('report_email') ?? '').trim(),
        })
      }}
    >
      <Field label="Наименование" width="m" error={problems.name}>
        <input
          className="field__input"
          name="name"
          defaultValue={organisation.name}
          aria-invalid={problems.name ? 'true' : undefined}
          required
        />
      </Field>
      {/* Короткое название - деньги, а не косметика: в сообщении должнику
          семьдесят знаков на кириллице, из них двадцать семь занимает адрес
          страницы. Полное наименование не оставляет от бюджета ничего, и волна
          уходит в две части по двойной цене (решение владельца 29.09.2026). */}
      <Field
        label="Короткое название"
        width="s"
        error={problems.short_name}
        hint="Им организация подписывает сообщения должникам. До девяти знаков - тогда сообщение уходит одной частью; длиннее - волна стоит вдвое. Пусто - берётся наименование целиком."
      >
        <input
          className="field__input"
          name="short_name"
          maxLength={32}
          defaultValue={organisation.short_name ?? ''}
          placeholder={organisation.name}
        />
      </Field>
      <Field label="ИНН" width="s" hint="Без него заявление вернут." error={problems.inn}>
        <input className="field__input" name="inn" defaultValue={organisation.inn ?? ''} />
      </Field>
      <Field
        label="Код региона"
        width="xs"
        error={problems.region_code}
        hint="По нему берутся праздники и часовой пояс - от них зависят сроки."
      >
        <input
          className="field__input"
          name="region_code"
          maxLength={2}
          defaultValue={organisation.region_code ?? ''}
        />
      </Field>
      {/* Якорь, а не просто поле: с экрана загрузки выгрузки 1С сюда ведёт
          кнопка «Изменить». Оператор, упёршийся в незаполненный срок, попадает
          прямо на него, а не на вкладку настроек вообще. */}
      {/* Подпись короткая намеренно: «Срок внесения платы» не влезает в узкое
          поле и переносится на две строки, а поле шире двузначного числа
          противоречит правилу ширины (осмотр 17.09.2026). Полностью величина
          объяснена пояснением под полем. */}
      <Field
        label="Срок оплаты"
        width="xs"
        id="payment-due-day"
        error={dayError ?? problems.payment_due_day}
        hint="Число месяца, следующего за расчётным, по договору управления. От него считаются пени и исковая давность. Для новых организаций стоит десятое число (ч. 1 ст. 155 ЖК РФ): договор может устанавливать другое - тогда исправьте. Если поле пусто, начисления загрузятся, но пени и давность по ним не посчитаются, а судебный пакет не соберётся; заполнили после загрузки - выгрузку нужно повторить."
      >
        <input
          className="field__input"
          name="payment_due_day"
          inputMode="numeric"
          aria-invalid={dayError ?? problems.payment_due_day ? 'true' : undefined}
          defaultValue={organisation.payment_due_day ?? ''}
        />
      </Field>
      <Field
        label="Куда присылать отчёт"
        width="m"
        error={problems.report_email}
        hint="Раз в месяц, с 1 по 10 число, на этот адрес приходит отчёт за прошлый месяц файлом PDF: что сделано, что вернулось, что горит и какие решения ждут. Пусто - письма не приходят, отчёт остаётся в разделе «Отчёты»."
      >
        <input
          className="field__input"
          name="report_email"
          type="email"
          inputMode="email"
          autoComplete="off"
          defaultValue={organisation.report_email ?? ''}
          aria-invalid={problems.report_email ? 'true' : undefined}
        />
      </Field>
      <button className="button" type="submit">
        {saving ? 'Сохраняем…' : 'Сохранить карточку'}
      </button>
    </LockedForm>
  )
}
